import { z } from "zod";

export const aiCapabilitySchema = z.enum([
  "speech_recognition",
  "speech_synthesis",
  "image_generation",
  "text_generation",
]);
export type AiCapability = z.infer<typeof aiCapabilitySchema>;

export const aiProviderStatusSchema = z.enum(["active", "disabled"]);
export const aiModelStatusSchema = z.enum(["draft", "published", "disabled"]);
export const aiProviderGatewaySchema = z.enum(["demo", "openai_compatible", "anthropic_compatible"]);
export type AiProviderGateway = z.infer<typeof aiProviderGatewaySchema>;

const timestamp = z.string().datetime();
const idSchema = z.string().trim().min(8).max(128);
const modelKeySchema = z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9._/-]+$/);
const credentialRefSchema = z.string().trim().max(256).refine(
  (value) => value === "" || /^(env|secret):\/\/[A-Za-z0-9_./-]+$/.test(value),
  "凭据引用格式示例：env://LK888_API_KEY",
);
const apiKeySchema = z.string().max(4096).default("");
const providerBaseUrlSchema = z.string().trim().max(256).transform((value) => value.replace(/\/+$/, "")).refine(
  (value) => value === "" || /^https:\/\/[A-Za-z0-9.-]+(?::[0-9]{2,5})?(?:\/[A-Za-z0-9._~/-]*)?$/.test(value),
  "接口根地址必须是 https，且不含账号或查询参数",
);

function requireBaseUrlForLiveGateway(gateway: AiProviderGateway, baseUrl: string, addIssue: (message: string) => void): void {
  if (gateway !== "demo" && !baseUrl) addIssue("真实供应商需要填写接口根地址，例如 https://api.lk888.ai/v1");
}

export const adminAiProviderSchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(80),
  code: z.string().min(1).max(64).regex(/^[a-z][a-z0-9_-]{1,63}$/),
  status: aiProviderStatusSchema,
  gateway: aiProviderGatewaySchema,
  baseUrl: z.string(),
  credentialConfigured: z.boolean(),
  credentialRef: z.string(),
  version: z.number().int().positive(),
  updatedAt: timestamp,
});
export type AdminAiProvider = z.infer<typeof adminAiProviderSchema>;

export const adminAiProviderCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  code: z.string().trim().min(1).max(64).regex(/^[a-z][a-z0-9_-]{1,63}$/),
  gateway: aiProviderGatewaySchema.default("demo"),
  baseUrl: providerBaseUrlSchema.default(""),
  credentialRef: credentialRefSchema.default(""),
  apiKey: apiKeySchema,
}).strict().superRefine((value, ctx) => {
  requireBaseUrlForLiveGateway(value.gateway, value.baseUrl, (message) => ctx.addIssue({ code: "custom", path: ["baseUrl"], message }));
});
export type AdminAiProviderCreateInput = z.infer<typeof adminAiProviderCreateSchema>;

export const adminAiProviderUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  status: aiProviderStatusSchema,
  gateway: aiProviderGatewaySchema,
  baseUrl: providerBaseUrlSchema,
  credentialRef: z.string().trim().max(256).refine(
    (value) => value === "" || value.includes("***") || /^(env|secret):\/\/[A-Za-z0-9_./-]+$/.test(value),
    "凭据引用格式示例：env://LK888_API_KEY",
  ).default(""),
  apiKey: apiKeySchema,
  expectedVersion: z.number().int().positive(),
}).strict().superRefine((value, ctx) => {
  requireBaseUrlForLiveGateway(value.gateway, value.baseUrl, (message) => ctx.addIssue({ code: "custom", path: ["baseUrl"], message }));
});
export type AdminAiProviderUpdateInput = z.infer<typeof adminAiProviderUpdateSchema>;

export const adminAiModelSchema = z.object({
  id: idSchema,
  providerId: idSchema,
  providerName: z.string().min(1),
  name: z.string().min(1).max(80),
  nameUg: z.string().max(80).default(""),
  modelKey: modelKeySchema,
  capability: aiCapabilitySchema,
  status: aiModelStatusSchema,
  version: z.number().int().positive(),
  updatedAt: timestamp,
});
export type AdminAiModel = z.infer<typeof adminAiModelSchema>;

export const adminAiModelCreateSchema = z.object({
  providerId: idSchema,
  name: z.string().trim().min(1, "请输入中文名称").max(80),
  nameUg: z.string().trim().min(1, "请输入维吾尔语名称").max(80),
  modelKey: modelKeySchema,
  capability: aiCapabilitySchema,
}).strict();
export type AdminAiModelCreateInput = z.infer<typeof adminAiModelCreateSchema>;

export const adminAiModelUpdateSchema = z.object({
  name: z.string().trim().min(1, "请输入中文名称").max(80),
  nameUg: z.string().trim().min(1, "请输入维吾尔语名称").max(80),
  modelKey: modelKeySchema,
  capability: aiCapabilitySchema,
  expectedVersion: z.number().int().positive(),
}).strict();
export type AdminAiModelUpdateInput = z.infer<typeof adminAiModelUpdateSchema>;

export const adminAiModelPublishSchema = z.object({
  expectedVersion: z.number().int().positive(),
}).strict();

export const adminAiGrantSchema = z.object({
  appId: z.string().min(1).max(128),
  appName: z.string().min(1),
  appCode: z.string().min(1),
  modelId: idSchema,
  modelName: z.string().min(1),
  capability: aiCapabilitySchema,
  enabled: z.boolean(),
  monthlyQuota: z.number().int().positive().nullable(),
  usedThisMonth: z.number().int().nonnegative(),
  version: z.number().int().positive(),
  updatedAt: timestamp,
});
export type AdminAiGrant = z.infer<typeof adminAiGrantSchema>;

export const adminAiGrantUpsertSchema = z.object({
  appId: z.string().trim().min(1).max(128),
  modelId: idSchema,
  enabled: z.boolean(),
  monthlyQuota: z.number().int().positive().max(10_000_000).nullable(),
  expectedVersion: z.number().int().nonnegative(),
}).strict();
export type AdminAiGrantUpsertInput = z.infer<typeof adminAiGrantUpsertSchema>;

export const adminAiUsageSchema = z.object({
  appId: z.string().min(1),
  appName: z.string().min(1),
  modelId: idSchema,
  modelName: z.string().min(1),
  capability: aiCapabilitySchema,
  period: z.string().regex(/^\d{4}-\d{2}$/),
  callCount: z.number().int().nonnegative(),
  unitCount: z.number().int().nonnegative(),
});
export type AdminAiUsage = z.infer<typeof adminAiUsageSchema>;

export const adminAiUsageQuerySchema = z.object({
  appId: z.string().default(""),
  period: z.string().regex(/^(\d{4}-\d{2})?$/).default(""),
}).strict();
export type AdminAiUsageQuery = z.infer<typeof adminAiUsageQuerySchema>;

export const openAiModelSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  nameUg: z.string().default(""),
  modelKey: z.string().min(1),
  capability: aiCapabilitySchema,
  monthlyQuota: z.number().int().positive().nullable(),
  usedThisMonth: z.number().int().nonnegative(),
});
export type OpenAiModel = z.infer<typeof openAiModelSchema>;

export const aiInvokeInputSchema = z.object({
  modelId: idSchema,
  prompt: z.string().trim().min(1).max(8000),
  inputUrl: z.string().trim().url().max(2000).nullable().optional(),
}).strict();
export type AiInvokeInput = z.infer<typeof aiInvokeInputSchema>;

export const adminAiInvokeInputSchema = aiInvokeInputSchema.extend({
  appId: z.string().trim().min(1).max(128),
}).strict();
export type AdminAiInvokeInput = z.infer<typeof adminAiInvokeInputSchema>;

export const aiInvokeResultSchema = z.object({
  id: idSchema,
  modelId: idSchema,
  capability: aiCapabilitySchema,
  demo: z.boolean(),
  text: z.string().nullable(),
  mediaUrl: z.string().nullable(),
  usage: z.object({
    period: z.string(),
    callCount: z.number().int().nonnegative(),
    unitCount: z.number().int().nonnegative(),
    monthlyQuota: z.number().int().positive().nullable(),
  }),
});
export type AiInvokeResult = z.infer<typeof aiInvokeResultSchema>;
